import { Component, ContentChildren, Input, QueryList, forwardRef, OnDestroy, SimpleChanges } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { trigger, style, transition, animate } from "@angular/animations";
import { Subscription } from "rxjs";
import { DsStepComponent } from "../ds-step/ds-step.component";
import { StepperOrientation, StepperNavigationMode } from "../types/stepper.types";

@Component({
  selector: "ds-stepper",
  templateUrl: "./ds-stepper.component.html",
  styleUrls: ["./ds-stepper.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsStepperComponent),
      multi: true,
    },
  ],
  animations: [
    trigger("contentAnimation", [
      transition("* => *", [
        style({ opacity: 0, transform: "translateY(10px)" }),
        animate("200ms ease-out", style({ opacity: 1, transform: "translateY(0)" })),
      ]),
    ]),
  ],
})
export class DsStepperComponent implements OnDestroy, ControlValueAccessor {
  @ContentChildren(DsStepComponent) steps!: QueryList<DsStepComponent>;
  @Input() orientation: StepperOrientation = "horizontal";
  @Input() navigationMode: StepperNavigationMode = "navigable";
  /** Renders the stepper as a progress indicator: no projected content, header takes the full width and labels wrap. */
  @Input() indicatorOnly = false;

  selectedIndex = 0;
  private _steps: DsStepComponent[] = [];
  private _completedSteps: Set<number> = new Set();
  private _onChange = (value: any) => {};
  private _onTouched = () => {};
  private _subscriptions: Subscription[] = [];
  private _previousCompletedStates: Map<number, boolean> = new Map();

  ngOnDestroy() {
    this._steps = [];
    this._subscriptions.forEach((sub) => sub.unsubscribe());
    this._subscriptions = [];
  }

  ngAfterContentInit() {
    this._steps = this.steps.toArray();
    this.updateSteps();
    this.subscribeToStepChanges();
  }

  ngAfterContentChecked() {
    const steps = this.steps.toArray();
    if (steps.length !== this._steps.length) {
      this._subscriptions.forEach((sub) => sub.unsubscribe());
      this._subscriptions = [];

      this._steps = steps;
      this.updateSteps();
      this.subscribeToStepChanges();
      return;
    }

    let hasChanges = false;
    this._steps.forEach((step, index) => {
      if (step.hasCustomCompletedBinding) {
        const previousCompleted = this._previousCompletedStates.get(index);
        const currentCompleted = step.completed;

        if (previousCompleted !== currentCompleted) {
          this._previousCompletedStates.set(index, currentCompleted);
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      this.updateSteps();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["navigationMode"]) {
      this.updateSteps();
    }
  }

  public updateSteps() {
    this._steps.forEach((step, index) => {
      step.stepIndex = index;
      step.isSelected = index === this.selectedIndex;
      if (step.hasCustomCompletedBinding) {
        step.isCompleted = step.completed;
        this._previousCompletedStates.set(index, step.completed);
      } else if (step.stepControl) {
        step.isCompleted = step.stepControl.valid;
      } else {
        step.isCompleted = step.completed || this._completedSteps.has(index);
      }

      step.isDisabled = this.isStepEditable(index, true);
    });
  }

  private isStepEditable(index: number, checkDisabled = false): boolean {
    const currentStep = this._steps[index];
    if (
      currentStep &&
      ((currentStep.editable === false && !checkDisabled && index < this.selectedIndex) ||
        (checkDisabled && currentStep.disabled))
    ) {
      return false;
    }

    if (this.navigationMode === "navigable") {
      return true;
    }

    if (index === 0) {
      return true;
    }

    for (let i = 0; i < index; i++) {
      const step = this._steps[i];
      if (!step) {
        return false;
      }

      if (step.stepControl) {
        if (!step.stepControl.valid) {
          return false;
        }
      } else if (step.hasCustomCompletedBinding) {
        if (!step.completed) {
          return false;
        }
      } else if (index > this.selectedIndex + 1 && !this._completedSteps.has(i)) {
        return false;
      }
    }

    return true;
  }

  private canNavigateToStep(index: number): boolean {
    if (this.navigationMode === "navigable") {
      return true;
    }

    if (index !== this.selectedIndex) {
      return this.isStepEditable(index);
    }

    return true;
  }

  private hasCustomCompletedBinding(step: DsStepComponent): boolean {
    return step.hasCustomCompletedBinding;
  }

  private subscribeToStepChanges() {
    this._steps.forEach((step, index) => {
      if (step.stepControl) {
        const subscription = step.stepControl.statusChanges.subscribe(() => {
          this.updateSteps();
        });
        this._subscriptions.push(subscription);
      }
    });
  }

  selectStep(index: number) {
    if (index < 0 || index >= this._steps.length) {
      return;
    }

    if (!this.isStepEditable(index)) {
      return;
    }

    if (!this.canNavigateToStep(index)) {
      return;
    }

    const previouslySelectedIndex = this.selectedIndex;
    if (previouslySelectedIndex !== index && previouslySelectedIndex >= 0) {
      const previousStep = this._steps[previouslySelectedIndex];
      if (previousStep && !this.hasCustomCompletedBinding(previousStep) && !previousStep.stepControl) {
        this._completedSteps.add(previouslySelectedIndex);
      }
    }

    this.selectedIndex = index;

    this.updateSteps();
    this._onChange(this.selectedIndex);
    this._onTouched();
  }

  next() {
    if (this.selectedIndex < this._steps.length - 1) {
      this.selectStep(this.selectedIndex + 1);
    }
  }

  previous() {
    if (this.selectedIndex > 0) {
      this.selectStep(this.selectedIndex - 1);
    }
  }

  reset() {
    this.selectedIndex = 0;
    this._completedSteps.clear();
    this._previousCompletedStates.clear();
    this.updateSteps();
    this._onChange(this.selectedIndex);
    this._onTouched();
  }

  writeValue(value: number): void {
    if (value !== null && value !== undefined) {
      this.selectedIndex = value;
      this.updateSteps();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  getStepLabel(index: number): string {
    return this._steps[index].label;
  }
}
